import React, { useMemo, useState } from 'react';
import ButtonToggle from 'plaid-threads/ButtonToggle';
import TableGroup from 'plaid-threads/TableGroup';
import ThreadsSearchInput from 'threads5/SearchInput';

import LinkedCode from 'src/components/docs/LinkedCode';

import styles from './RateLimitTable.module.scss';

export const ROWS_PER_PAGE = 15;

type Environment = 'production' | 'sandbox';

const ENVIRONMENT_LABELS: Record<Environment, string> = {
  production: 'Production',
  sandbox: 'Sandbox',
};

const ENV_TOGGLE_OPTIONS: Array<{ label: string; value: Environment }> = [
  { label: ENVIRONMENT_LABELS.production, value: 'production' },
  { label: ENVIRONMENT_LABELS.sandbox, value: 'sandbox' },
];

interface LimitCell {
  display: string;
  limits: Array<unknown>;
}

export interface RateLimitRow {
  path: string;
  display_path: string;
  production: {
    item: LimitCell;
    client: LimitCell;
  };
  sandbox: {
    item: LimitCell;
    client: LimitCell;
  };
  // Set by scripts/filter-rate-limits.js for rows whose docs target is gated
  // by an LD flag. Consumers (e.g. RateLimitTableWithFlagFilter) evaluate the
  // flag at render time and hide the row from visitors who lack access, so the
  // table never advertises a row whose linked page they cannot reach.
  gating_flag?: string;
}

interface Props {
  // Rows to render. Caller is responsible for filtering dead links — the
  // canonical caller is src/pages/docs/errors/rate-limit-exceeded.mdx,
  // which imports src/data/generated/rate-limits-filtered.json (produced by
  // scripts/filter-rate-limits.js at build time) and threads `rateLimits.rows`
  // to this component as well as to <RateLimitFor> for the inline per-error
  // blurbs on the same page.
  rows: RateLimitRow[];
  /** Which environment is selected before the user touches the toggle. */
  initialEnvironment?: Environment;
}

const COLUMNS = [
  {
    Header: 'Endpoint',
    accessor: 'endpoint',
    setWidth: '44%',
  },
  {
    Header: 'Max requests per Item',
    accessor: 'perItem',
  },
  {
    Header: 'Max requests per client',
    accessor: 'perClient',
  },
];

const RateLimitTable = ({
  rows: rowsProp,
  initialEnvironment = 'production',
}: Props): React.ReactElement => {
  const [environment, setEnvironment] =
    useState<Environment>(initialEnvironment);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const selectedToggleOption = useMemo(() => {
    return (
      ENV_TOGGLE_OPTIONS.find((o) => o.value === environment) ??
      ENV_TOGGLE_OPTIONS[0]
    );
  }, [environment]);

  const rows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rowsProp
      .filter((row) => {
        const limits = row[environment];
        if (limits.item.display === 'N/A' && limits.client.display === 'N/A') {
          return false;
        }
        if (query && !row.display_path.toLowerCase().includes(query)) {
          return false;
        }
        return true;
      })
      .map((row) => {
        const limits = row[environment];
        return {
          id: `${environment}:${row.path}`,
          endpoint: <LinkedCode>{row.display_path}</LinkedCode>,
          perItem: limits.item.display,
          perClient: limits.client.display,
        };
      });
  }, [environment, searchQuery, rowsProp]);

  const environmentLabel = ENVIRONMENT_LABELS[environment];
  const tableAccessibleName = `Rate limits (${environmentLabel})`;

  return (
    <TableGroup
      key={environment}
      label={tableAccessibleName}
      hasLabel={false}
      className={styles.table}
      columns={COLUMNS}
      rows={rows}
      hasPagination
      pagination={{ rowsPerPage: ROWS_PER_PAGE }}
      search={
        <div
          className={styles.toolbar}
          data-suppress-docs-slash-search
          data-testid='rate-limit-toolbar'
        >
          <div className={styles.searchField} data-testid='rate-limit-filter'>
            <ThreadsSearchInput
              className='search-input'
              classes={{}}
              state='default'
              id='rate-limit-search'
              label='Search endpoints by path'
              placeholder='Search endpoints by path'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClearButtonClick={() => setSearchQuery('')}
              inputProps={{ spellCheck: false }}
              size='40'
              sx={{
                // Same `sx` shape as `CoverageExplorer` (`TableGroup` search slot).
                fontSize: '1.6rem',
                margin: '0',
                padding: '0',
                width: '100%',
                '& .MuiFormControl-root': {
                  marginTop: 0,
                  marginBottom: 0,
                },
                '& input': {
                  border: 'none',
                  padding: '0',
                  margin: '0',
                  '&:focus': {
                    outline: 'none',
                    border: 'none',
                    boxShadow: 'none',
                  },
                },
              }}
            />
          </div>
          <div
            className={styles.toggleSlot}
            data-testid='rate-limit-env-toggle'
          >
            <ButtonToggle
              options={ENV_TOGGLE_OPTIONS}
              value={selectedToggleOption}
              onChange={(opt) => {
                setEnvironment(opt.value as Environment);
              }}
            />
          </div>
        </div>
      }
    />
  );
};

export default RateLimitTable;
